import { z } from 'zod';
/**
 * Zod mirrors of the domain model. These are the wire format: every client and
 * the server validate against exactly these schemas, so a contract change is a
 * compile-time and runtime failure rather than a silent divergence.
 */
export const categorySchema = z.enum(['basketball', 'football', 'baseball', 'hockey', 'soccer', 'tcg', 'other']);
export const serialNumberingSchema = z.object({
    copyNumber: z.number().int().positive().nullable(),
    printRun: z.number().int().positive().nullable(),
    isOneOfOne: z.boolean(),
});
export const cardIdentitySchema = z.object({
    subject: z.string().min(1).max(120).nullable(),
    year: z.number().int().min(1860).max(2100).nullable(),
    seasonLabel: z.string().max(16).nullable(),
    manufacturer: z.string().max(80).nullable(),
    setName: z.string().max(120).nullable(),
    subset: z.string().max(120).nullable(),
    cardNumber: z.string().max(24).nullable(),
    parallel: z.string().max(80).nullable(),
    serial: serialNumberingSchema.nullable(),
    isAutograph: z.boolean(),
    isRelic: z.boolean(),
    isRookie: z.boolean(),
    isShortPrint: z.boolean(),
    category: categorySchema,
    variation: z.string().max(160).nullable(),
    language: z.string().max(5).nullable(),
    team: z.string().max(80).nullable(),
});
export const partialCardIdentitySchema = cardIdentitySchema.partial();
export const gradingCompanySchema = z.enum(['PSA', 'BGS', 'SGC', 'CGC', 'CSG', 'HGA', 'TAG', 'ACE', 'ISA', 'OTHER']);
export const bgsSubgradesSchema = z.object({
    centering: z.number().min(1).max(10).nullable(),
    corners: z.number().min(1).max(10).nullable(),
    edges: z.number().min(1).max(10).nullable(),
    surface: z.number().min(1).max(10).nullable(),
});
export const cardGradeSchema = z.object({
    company: gradingCompanySchema,
    grade: z.number().min(0.5).max(10).nullable(),
    label: z.string().max(60).nullable(),
    qualifier: z.string().max(20).nullable(),
    certNumber: z.string().max(24).nullable(),
    subgrades: bgsSubgradesSchema.nullable(),
    isAuthenticOnly: z.boolean(),
    autoGrade: z.number().min(1).max(10).nullable(),
});
export const rawConditionEstimateSchema = z.enum([
    'gem_mint',
    'mint',
    'near_mint',
    'excellent',
    'very_good',
    'good',
    'poor',
    'unknown',
]);
export const cardConditionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('graded'), grade: cardGradeSchema }),
    z.object({ kind: z.literal('raw'), estimate: rawConditionEstimateSchema, notes: z.string().max(400).nullable() }),
    z.object({ kind: z.literal('unknown') }),
]);
export const moneySchema = z.object({
    amountMinor: z.number().int(),
    currency: z.string().length(3),
});
export const provenanceSchema = z.object({
    provider: z.string(),
    fetchedAt: z.string(),
    licence: z.string(),
    cacheTtlSeconds: z.number().int().nonnegative(),
    sourceUrl: z.string().nullable(),
    displayUrlAllowed: z.boolean(),
    displayIndividualRecords: z.boolean(),
});
export const marketSaleSchema = z.object({
    kind: z.literal('sold'),
    id: z.string(),
    source: z.enum([
        'ebay_marketplace_insights',
        'ebay_browse',
        'pricecharting',
        'partner_feed',
        'auction_house_archive',
        'user_submitted',
        'manual_entry',
        'fixture',
    ]),
    marketplace: z.string(),
    externalId: z.string(),
    title: z.string(),
    identity: cardIdentitySchema,
    condition: cardConditionSchema,
    price: moneySchema,
    shipping: moneySchema.nullable(),
    buyerFees: moneySchema.nullable(),
    total: moneySchema,
    soldAt: z.string(),
    saleType: z.enum(['auction', 'buy_it_now', 'best_offer', 'private_sale', 'unknown']),
    quantity: z.number().int().positive(),
    imageUrl: z.string().nullable(),
    provenance: provenanceSchema,
    parseConfidence: z.number().min(0).max(1),
});
export const auctionContextSchema = z.object({
    platform: z.string().max(60),
    currentBid: moneySchema.nullable(),
    currency: z.string().length(3),
    buyerPremiumRate: z.number().min(0).max(0.5),
    shippingEstimate: moneySchema.nullable(),
    salesTaxRate: z.number().min(0).max(0.3),
    observedAt: z.string(),
    sourceUrl: z.string().nullable(),
});
export const frameMetricsSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sharpness: z.number().nonnegative(),
    brightness: z.number().min(0).max(255),
    clippedHighlightRatio: z.number().min(0).max(1),
    clippedShadowRatio: z.number().min(0).max(1),
    motionScore: z.number().min(0).max(255).nullable(),
    cardCoverage: z.number().min(0).max(1).nullable(),
    occlusionScore: z.number().min(0).max(1).nullable(),
    perceptualHash: z.string().max(32).nullable(),
});
export const buyerPreferencesSchema = z.object({
    riskProfile: z.enum(['conservative', 'balanced', 'aggressive']),
    intent: z.enum(['collect', 'flip']),
    targetDiscount: z.number().min(0).max(0.9).nullable(),
    sellFeeRate: z.number().min(0).max(0.5),
    sellShipping: moneySchema.nullable(),
    maxSpend: moneySchema.nullable(),
});
//# sourceMappingURL=domain-schemas.js.map